import { useNavigate } from 'react-router-dom';
import type { Instance } from '@neburst-bot/shared';
import { Server, Wifi, WifiOff } from 'lucide-react';
import { useInstanceMetrics, useInstancesStream } from '../api/hooks';
import {
  Avatar,
  Cell,
  Chip,
  EmptyState,
  PageHeader,
  ProgressBar,
  Section,
  Skeleton,
  StatusDot,
} from '../components/ui';
import { useT } from '../i18n';
import { statusLabel } from '../lib/status';
import { flagEmoji } from '../lib/flag';

function InstanceRow({
  inst,
  last,
  onOpen,
}: {
  inst: Instance;
  last: boolean;
  onOpen: () => void;
}) {
  const t = useT();
  const status = pickCode(inst.state) ?? pickCode(inst.status) ?? 'unknown';
  const regionDisplay = pickDisplay(inst.region) ?? pickCode(inst.region) ?? t('common.dash');
  const shape = pickDisplay(inst.shape) ?? pickCode(inst.shape) ?? t('common.dash');
  const name = inst.name ?? inst.hostname ?? inst.uuid.slice(0, 8);
  const flag = flagEmoji(inst.region?.indicator);

  const metrics = useInstanceMetrics(inst.uuid);
  const bw = metrics.data?.bandwidth;
  // Match dashboard: usage (egress) over limit, both normalized to GB.
  const usageRaw = bw?.usage ?? bw?.outbound ?? 0;
  const usedGB = bw?.usage_unit === 'TB' ? usageRaw * 1000 : usageRaw;
  const limitGB = bw?.limit != null ? (bw.limit_unit === 'TB' ? bw.limit * 1000 : bw.limit) : 0;
  const pct = bw && limitGB > 0 ? Math.min(100, (usedGB / limitGB) * 100) : null;

  return (
    <Cell
      onClick={onOpen}
      last={last}
      leading={
        flag ? <Avatar emoji={flag} label={regionDisplay} /> : <Avatar label={regionDisplay} />
      }
      title={name}
      subtitle={
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: 5,
            minWidth: 0,
          }}
        >
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 6,
              color: 'var(--tg-hint)',
              fontSize: 12.5,
            }}
          >
            <span>{shape}</span>
            {pct != null && (
              <>
                <span style={{ opacity: 0.5 }}>·</span>
                <span
                  style={{
                    fontFeatureSettings: '"tnum"',
                    color:
                      pct >= 90
                        ? 'var(--danger)'
                        : pct >= 70
                          ? 'var(--warning)'
                          : 'var(--tg-hint)',
                  }}
                >
                  {t('vps.list.trafficUsed', { pct: pct.toFixed(1) })}
                </span>
              </>
            )}
          </div>
          {pct != null && (
            <div style={{ width: '100%', maxWidth: 220 }}>
              <ProgressBar pct={pct} />
            </div>
          )}
        </div>
      }
      trailing={
        <Chip
          color={
            status === 'running'
              ? 'success'
              : status === 'stopped'
                ? 'neutral'
                : status === 'error'
                  ? 'danger'
                  : 'warning'
          }
          leading={<StatusDot color="currentColor" pulse={status === 'running'} />}
        >
          {statusLabel(t, status)}
        </Chip>
      }
    />
  );
}

function pickCode(v: unknown): string | undefined {
  if (typeof v === 'string') return v;
  if (v && typeof v === 'object') {
    const o = v as Record<string, unknown>;
    if (typeof o.code === 'string') return o.code;
  }
  return undefined;
}
function pickDisplay(v: unknown): string | undefined {
  if (v && typeof v === 'object') {
    const o = v as Record<string, unknown>;
    if (typeof o.display === 'string') return o.display;
  }
  return undefined;
}

export function InstanceList() {
  const navigate = useNavigate();
  const t = useT();
  const { instances, error } = useInstancesStream();

  const running = instances.filter(
    (i) => (pickCode(i.state) ?? pickCode(i.status)) === 'running',
  ).length;
  const loading = instances.length === 0 && !error;

  return (
    <>
      <PageHeader
        title={t('vps.list.title')}
        subtitle={
          loading
            ? t('common.connecting')
            : error
              ? t('common.connectError')
              : t('vps.list.subtitleCount', { total: instances.length, running })
        }
        right={
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 6,
              fontSize: 12,
              color: error ? 'var(--danger)' : 'var(--success)',
              fontWeight: 600,
            }}
          >
            {error ? <WifiOff size={14} /> : <Wifi size={14} />}
            {error ? t('common.offline') : t('common.live')}
          </div>
        }
      />

      {error && (
        <Section>
          <div style={{ padding: 14, color: 'var(--danger)', fontSize: 13 }}>{error}</div>
        </Section>
      )}

      <Section>
        {loading &&
          [0, 1, 2].map((i) => (
            <div
              key={i}
              style={{
                padding: '14px 16px',
                display: 'flex',
                gap: 12,
                borderBottom: i < 2 ? '0.5px solid var(--tg-separator)' : 'none',
              }}
            >
              <Skeleton width={38} height={38} radius={19} />
              <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 6 }}>
                <Skeleton width="60%" height={14} />
                <Skeleton width="80%" height={12} />
              </div>
            </div>
          ))}
        {instances.map((i, idx) => (
          <InstanceRow
            key={i.uuid}
            inst={i}
            last={idx === instances.length - 1}
            onOpen={() => navigate(`/instances/${i.uuid}`)}
          />
        ))}
        {!loading && instances.length === 0 && !error && (
          <EmptyState
            icon={<Server size={28} />}
            title={t('vps.list.empty.title')}
            description={t('vps.list.empty.desc')}
          />
        )}
      </Section>
    </>
  );
}
